import { cn } from "~/lib/utils";

// // // //

type ButtonVariant = `${ButtonVariants}`;
export enum ButtonVariants {
    primary = "primary",
    primaryLink = "primaryLink",
    primaryOutline = "primary-outline",
    andOperatorLight = "and-operator-light",
    orOperatorLight = "or-operator-light",
    navbarSecondary = "navbar-secondary",
    navbarGhost = "navbar-ghost",
}

export function getButtonClassName(props: {
    variant?: ButtonVariant;
    disabled?: boolean;
    size?: "xs" | "sm" | "md" | "lg" | "xl";
    block?: boolean;
    rounded?: boolean | "sm" | "md" | "lg" | "xl" | "full";
    hasIcon?: boolean;
    className?: string;
}): string {
    const {
        variant = "primary",
        disabled = false,
        block = false,
        rounded = "md",
        hasIcon = false,
        size = "lg",
        className,
    } = props;

    const isPrimary = variant === ButtonVariants.primary;
    const isPrimaryOutline = variant === ButtonVariants.primaryOutline;
    const isAndOperatorLight = variant === ButtonVariants.andOperatorLight;
    const isOrOperatorLight = variant === ButtonVariants.orOperatorLight;
    const isNavbarSecondary = variant === ButtonVariants.navbarSecondary;
    const isNavbarGhost = variant === ButtonVariants.navbarGhost;

    // Link variations
    const isPrimaryLink = variant === ButtonVariants.primaryLink;
    const isLink = isPrimaryLink;

    return cn(
        className,
        "transition-all flex items-center whitespace-nowrap select-none",
        {
            // Hover Shadow CSS (apply for all except link variants)
            "hover:shadow-xs": !isLink,

            // Has Icon?
            "justify-between": hasIcon && !block,
            // "justify-between": hasIcon && block,
            "justify-center": !hasIcon || (hasIcon && block),

            // Variant = Primary
            "bg-linear-to-b from-slate-700 to-slate-800 text-white font-light border border-slate-800":
                isPrimary,
            "hover:bg-linear-to-b hover:from-slate-600 hover:to-slate-800":
                isPrimary && !disabled,

            // Variant = PrimaryOutline
            "bg-transparent border border-slate-800/40 text-slate-800":
                isPrimaryOutline,
            "hover:bg-slate-200": isPrimaryOutline && !disabled,

            // Variant = NavbarSecondary
            "bg-transparent border border-slate-500/60 text-slate-800 font-normal":
                isNavbarSecondary,
            "hover:bg-slate-200 hover:border-slate-500/60":
                isNavbarSecondary && !disabled,

            // Variant = NavbarGhost
            "bg-transparent border border-transparent shadow-none hover:shadow-none text-slate-800 font-normal":
                isNavbarGhost,
            "hover:bg-slate-200 hover:border-slate-400/60":
                isNavbarGhost && !disabled,

            // Variant = "and-operator-light"
            "bg-blue-100 text-blue-500": isAndOperatorLight,
            "hover:bg-blue-200": isAndOperatorLight && !disabled,

            // Variant = "or-operator-light"
            "bg-violet-100 text-violet-500": isOrOperatorLight,
            "hover:bg-violet-200": isOrOperatorLight && !disabled,

            // Variant = "primary-link"
            "text-link hover:text-link-hover": isPrimaryLink,

            // Disabled
            "cursor-not-allowed opacity-40": disabled,

            // Text Size
            "text-xs": size === "xs",
            "text-sm": size === "sm",
            "text-lg": size === "md",
            "text-xl": size === "lg",
            "text-2xl": size === "xl",

            // Padding Size
            "py-1 px-4": size === "xs" && !isLink,
            "py-1.5 px-5": size === "sm" && !isLink,
            "py-3 px-8": size === "md" && !isLink,
            "py-6 px-12": size === "lg" && !isLink,
            "py-8 px-14": size === "xl" && !isLink,

            // Border Radius
            // ENHANCEMENT - clean up this logic, for the love of god
            rounded:
                (rounded === false && ["xs", "sm", "md"].includes(size)) ||
                rounded === "md",
            "rounded-xs": rounded === "sm",
            "rounded-xl": rounded === "xl",
            "rounded-lg":
                (rounded === false && (size === "lg" || size === "xl")) ||
                rounded === "lg",
            "rounded-full": rounded == true || rounded === "full",

            // Block
            "w-full": block === true,
        },
    );
}
